<template>
  <!-- Aberta ao entrar: os filtros de dataset sao o principal da barra, e
       comecar recolhida escondia a existencia deles. O default do SideBarMenu
       continua fechado - so o Flow.Sight opta por abrir. -->
  <side-bar-menu width="315px" :initial-expanded="true">
    <!-- Page navigation. Lives in the sidebar's `nav` slot so it stays on
         screen when the sidebar is collapsed, as an icon rail. -->
    <template v-slot:nav="{ expanded }">
      <div class="nav-rail" :class="{ 'nav-rail--collapsed': !expanded }">
        <v-tooltip v-for="item in navItems" :key="item.link" right :disabled="expanded">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              :to="item.link"
              :disabled="item.disabled"
              text
              small
              class="nav-item"
              :class="{ 'nav-item--active': isNavActive(item), 'nav-item--icon': !expanded }"
            >
              <v-icon small :class="expanded ? 'mr-2' : ''">{{ item.icon }}</v-icon>
              <span v-if="expanded">{{ item.title }}</span>
            </v-btn>
          </template>
          <span>{{ item.title }}</span>
        </v-tooltip>
      </div>
    </template>

    <v-card outlined rounded-lg class="sidebar-section mx-2 elevation-0 pb-6 pt-0 mt-0">
      <v-card-actions class="d-flex">
        <v-icon>mdi-filter-outline</v-icon>
        <v-card-title class="strong" style="line-height: .7rem;font-size:1.5rem">{{ $t('instructions.filters')
          }}</v-card-title>
      </v-card-actions>
      <v-card-actions class="d-flex flex-column align-stretch mt-1 mx-0">
        <div class="sidebar-period">
          <h4>{{ $t('flowSightMenu.period') }}</h4>
        </div>
        <div class="d-flex flex-row mt-4">
          <v-menu v-model="menu" :disabled="!octaActive" :close-on-content-click="false" :nudge-right="40"
            transition="scale-transition" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-text-field v-model="tsMin" :disabled="!octaActive" :label="$t('extras.start')"
                prepend-icon="mdi-calendar" readonly dense v-bind="attrs" v-on="on" hide-details
                class="mr-0 mr-3 mb-2" />
            </template>
            <v-date-picker v-model="tsMin" no-title :disabled="!octaActive" color="primary"
              @input="val => handleTimeInput(val, true)" :locale="locale" />
          </v-menu>

          <v-menu :disabled="!octaActive" v-model="menu2" :close-on-content-click="false" :nudge-right="40"
            transition="scale-transition" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-text-field v-model="tsMax" :label="$t('extras.end')" prepend-icon="mdi-calendar" readonly dense
                :disabled="!octaActive" v-bind="attrs" v-on="on" hide-details class="mr-0 mb-2" />
            </template>
            <v-date-picker v-model="tsMax" no-title :disabled="!octaActive" :min="tsMin"
              @input="val => handleTimeInput(val, false)" color="primary" />
          </v-menu>
        </div>

        <div class="py-2 thin-border">
          <div class="sidebar-period">
            <h4>{{ $t('quoteInfo.side') }}</h4>
          </div>
          <div class="d-flex justify-space-between align-center">
            <v-switch color="primary" v-model="sideSwitchInternal" @change="checkSideSwitch('BUY')"
              :label="$t('flowSight.buy')" value="BUY" inset dense hide-details />
            <v-switch color="primary" v-model="sideSwitchInternal" @change="checkSideSwitch('SELL')"
              :label="$t('flowSight.sell')" value="SELL" inset dense hide-details />
          </div>
        </div>

        <div class="pt-4">
          <div class="sidebar-period">
            <h4>{{ $t('flowSightMenu.rfqPeriod') }}</h4>
          </div>
          <v-btn-toggle v-model="rfqPeriodInternal" mandatory dense class="period-toggle" color="primary">
            <v-btn v-for="opt in rfqPeriodOptions" :key="opt.value" :value="opt.value" small
              :disabled="!hasDataset">{{ opt.label }}</v-btn>
          </v-btn-toggle>
          <!-- The windows hang off the dataset's last RFQ, not today's date, so
               the anchor is spelled out - otherwise "Dia" is unreadable. -->
          <div v-if="anchorLabel" class="period-anchor">{{ $t('flowSightMenu.anchorLabel') }}: {{ anchorLabel }}</div>
        </div>

        <div class="pt-4">
          <div class="sidebar-period">
            <h4>{{ $t('flowSightMenu.benchmark') }}</h4>
          </div>
          <!-- Only the button matching the selected period is enabled, so the
               disabled state carries the rule on its own - no hint line. -->
          <v-btn-toggle v-model="benchmarkInternal" dense class="period-toggle" color="primary">
            <v-btn v-for="opt in benchmarkOptions" :key="opt.value" :value="opt.value" small
              :disabled="!isBenchmarkSelectable(opt.value)">{{ opt.label }}</v-btn>
          </v-btn-toggle>
          <!-- Which window the KPIs are being compared against. Mirrors the
               "Base:" line under the period buttons - without it the benchmark
               says "vs 2S" and never spells out which fortnight that is. -->
          <div v-if="benchmarkLabel" class="period-anchor">
            {{ $t('flowSightMenu.benchmarkLabel') }}: {{ benchmarkLabel }}
          </div>
        </div>

        <div class="pt-4">
          <div class="sidebar-period">
            <h4>{{ $t('flowSightMenu.counterparty') }}</h4>
          </div>
          <!-- autocomplete, not select: a real dataset carries ~100 names and
               scrolling to one of them is unusable without typing. -->
          <v-autocomplete
            v-model="counterpartyFilterInternal"
            :items="counterparties"
            :disabled="!counterparties.length"
            :placeholder="$t('flowSightMenu.counterpartyNone')"
            :no-data-text="$t('flowSightMenu.counterpartyNoData')"
            multiple
            dense
            hide-details
            outlined
            clearable
            :menu-props="{ maxHeight: 320, offsetY: true }"
          >
            <!-- Chips would wrap a 315px sidebar into a wall of text with
                 names this long, so the selection collapses into a count. With
                 every name ticked - the default - it says so instead of naming
                 one and adding "+57". -->
            <template v-slot:selection="{ item, index }">
              <span v-if="allCounterpartiesSelected && index === 0" class="counterparty-selection">
                {{ $t('flowSightMenu.counterpartyAll') }}
              </span>
              <template v-else-if="!allCounterpartiesSelected">
                <span v-if="index === 0" class="counterparty-selection">{{ item }}</span>
                <span v-else-if="index === 1" class="counterparty-more">
                  +{{ counterpartyFilterInternal.length - 1 }}
                </span>
              </template>
            </template>
            <template v-slot:prepend-item>
              <v-list-item ripple dense @mousedown.prevent @click="toggleAllCounterparties">
                <v-list-item-action>
                  <v-icon :color="someCounterpartiesSelected ? 'primary' : ''">{{ counterpartySelectAllIcon }}</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>{{ $t('flowSightMenu.counterpartyAll') }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-1"></v-divider>
            </template>
          </v-autocomplete>
        </div>
      </v-card-actions>

      <!-- <v-divider class="my-1"></v-divider>

      <v-card-actions class="d-flex flex-column align-start mx-0 px-0">
        <v-divider></v-divider>
        <div class="d-flex flex-column">
          <v-card-subtitle class="card-text-title">{{ $t('flowSightMenu.counterparty') }}</v-card-subtitle>
          <v-card-text class="card-text-desc">{{ $t('flowSightMenu.additionalFiltersNote') }}</v-card-text>
        </div>
      </v-card-actions>

      <v-divider></v-divider>
      <v-card-actions class="d-flex flex-column align-start mx-0 px-0">
        <v-divider></v-divider>
        <div class="d-flex flex-column">
          <v-card-subtitle class="card-text-title">{{ $t('flowSightMenu.currencyPair') }}</v-card-subtitle>
          <v-card-text class="card-text-desc">{{ $t('flowSightMenu.currencyPairHint') }}</v-card-text>
        </div>
      </v-card-actions>

      <v-divider></v-divider>
      <v-card-actions class="mx-0 px-0">
        <div class="d-flex flex-column">
          <v-card-subtitle class="card-text-title">{{ $t('quoteInfo.status') }}</v-card-subtitle>
          <v-card-text class="card-text-desc text-no-wrap">{{ $t('flowSightMenu.statusHint') }}</v-card-text>
        </div>
      </v-card-actions> -->
    </v-card>

    <v-card outlined rounded-lg class="elevation-0 mx-2 mt-4 pt-0 pb-3 sidebar-section mb-5">
      <v-card-actions class="d-flex">
        <v-icon>mdi-cog-outline</v-icon>
        <v-card-title class="strong" style="line-height: .7rem;font-size:1.5rem">{{ $t('toolbar.settings.title')
          }}</v-card-title>
      </v-card-actions>
      <div v-if="hasUploadedCsv" class="uploaded-file-info mx-4 mb-2">
        {{ $t('flowSightMenu.datasetFileName') }}: "{{ uploadedFileName }}", {{ $t('flowSightMenu.datasetLines') }}: {{ uploadedLineCount }}
      </div>
      <v-card-actions class="mx-0 px-0">
        <div class="d-flex flex-column align-center mx-auto justify-center flex-gap-4">
          <v-btn color="primary" :disabled="handleBlur" @click="$emit('open-modal')" small
            v-show="!isFlowSight">
            <v-icon small color="black" class="mx-1">mdi-upload</v-icon>
            <h5 style="color:black">{{ $t('analytics.loadDataset') }}</h5>
          </v-btn>
          <v-btn disabled small color="primary" outlined>
            <v-icon small color="primary" class="mx-1">mdi-file-import</v-icon>
            <h5>{{ $t('analytics.importDataset') }}</h5>
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>

    <!-- Seletor de produto. Existe porque a troca NAO pode mais vir do arquivo:
         subir um CSV fora do formato esperado da erro, e mudar de produto e um
         ato explicito do usuario. Trocar limpa o dataset - os dois relatorios
         nao compartilham colunas nem nocao de prazo. -->
    <v-card outlined rounded-lg class="elevation-0 mx-2 mt-4 pt-0 pb-2 sidebar-section">
      <v-card-actions class="d-flex">
        <v-icon>mdi-swap-horizontal</v-icon>
        <v-card-title class="strong" style="line-height:.7rem;font-size:1.1rem">
          {{ $t('flowSightMenu.product') }}
        </v-card-title>
      </v-card-actions>
      <div class="px-4 pb-1">
        <!-- Dois botoes soltos, e nao um v-btn-toggle: o toggle guarda estado
             proprio e piscaria para o outro produto antes da confirmacao, e nao
             volta sozinho se o usuario cancelar. Aqui o destaque vem so do
             store, entao a UI nunca mostra um produto que nao esta em vigor. -->
        <div class="produto-toggle d-flex">
          <v-btn
            v-for="p in produtos"
            :key="p.product"
            small
            text
            :disabled="handleBlur"
            class="produto-btn"
            :class="{ 'produto-btn--ativo': p.product === produtoAtual }"
            @click="pedirTroca(p.product)"
          >
            <v-icon small class="mr-1">{{ p.icon }}</v-icon>{{ p.name }}
          </v-btn>
        </div>
        <div v-if="temDataset" class="produto-aviso mt-1">
          {{ $t('flowSightMenu.productSwitchWarning') }}
        </div>
      </div>
    </v-card>

    <!-- Confirmacao da troca. So aparece quando ha dataset carregado - sem dado
         nao ha analise para perder, e um modal ali seria so atrito. -->
    <v-dialog v-model="confirmandoTroca" max-width="460" @click:outside="cancelarTroca">
      <v-card class="px-2 py-1">
        <v-card-title class="text-h6">
          <v-icon color="warning" class="mr-2">mdi-alert-outline</v-icon>
          {{ $t('flowSightMenu.productSwitchTitle') }}
        </v-card-title>
        <v-card-text>
          <p class="mb-2">
            {{ $t('flowSightMenu.productSwitchBody', { produto: nomeProdutoPendente }) }}
          </p>
          <p v-if="uploadedFileName" class="mb-0 produto-aviso">
            {{ $t('flowSightMenu.datasetFileName') }}: "{{ uploadedFileName }}"
          </p>
        </v-card-text>
        <v-card-actions class="d-flex justify-end">
          <v-btn text small @click="cancelarTroca">
            {{ $t('flowSightMenu.productSwitchCancel') }}
          </v-btn>
          <v-btn color="primary" small @click="confirmarTroca">
            {{ $t('flowSightMenu.productSwitchConfirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </side-bar-menu>
</template>

<script>
import { useAnalyticsProStore } from '@/store/analyticsStore'
import { FLOW_SIGHT_PRODUCTS } from '@/utils/flowSightProducts'
import { mapStores } from 'pinia'
import SideBarMenu from '@/components/layout/SideBarMenu.vue'

export default {
  components: { SideBarMenu },
  props: {
    maxSliderDigits: { type: Number },
    periodRange: { type: Object },
    sliderRange: { type: Array },
    sideSwitch: { type: Array },
    isPeriodSingleValue: { type: Boolean },
    isYtdShortcutApplied: { type: Boolean },
    isMtdShortcutApplied: { type: Boolean },
    navItems: { type: Array, default: () => [] },
    isFlowSight: {
      type: Boolean,
      default: false,
    },
    handleBlur: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'open-modal',
    'apply-ytd-shortcut',
    'apply-mtd-shortcut',
    'check-side-switch',
    'on-period-slider-change',
    'update:sideSwitch',
  ],
  data() {
    return {
      confirmandoTroca: false,
      produtoPendente: null,
      menu: null,
      menu2: null,
      tsMin: null,
      tsMax: null,
    }
  },
  computed: {
    ...mapStores(useAnalyticsProStore),
    produtos() {
      return FLOW_SIGHT_PRODUCTS
    },
    nomeProdutoPendente() {
      const p = FLOW_SIGHT_PRODUCTS.find((x) => x.product === this.produtoPendente)
      return p ? p.name : ''
    },
    produtoAtual() {
      return this.analyticsProStore.flowSightProduct
    },
    temDataset() {
      return (this.analyticsProStore.getAnalytics || []).length > 0
    },
    sideSwitchInternal: {
      get() { return this.sideSwitch },
      set(v) { this.$emit('update:sideSwitch', v) },
    },
    locale() {
      // Follows the UI language. Defaulting to 'en' here while i18n.js defaults
      // to 'pt' is what rendered dd/mm dates as mm/dd on a Portuguese screen.
      return localStorage.getItem('userLanguage') || this.$i18n.locale
    },
    octaActive() {
      return this.analyticsProStore.getOctaActive
    },
    hasUploadedCsv() {
      return this.analyticsProStore.hasUploadedCsv
    },
    uploadedFileName() {
      return this.analyticsProStore.getUploadedFileName
    },
    uploadedLineCount() {
      return this.analyticsProStore.getAnalytics.length
    },
    hasDataset() {
      return this.analyticsProStore.getAnalytics.length > 0
    },
    rfqPeriodOptions() {
      return [
        { value: 'DAY', label: this.$t('flowSightMenu.rfqPeriodDay') },
        { value: 'WEEK', label: this.$t('flowSightMenu.rfqPeriodWeek') },
        { value: 'FORTNIGHT', label: this.$t('flowSightMenu.rfqPeriodFortnight') },
        { value: 'MONTH', label: this.$t('flowSightMenu.rfqPeriodMonth') },
        { value: 'ALL', label: this.$t('flowSightMenu.rfqPeriodAll') },
      ]
    },
    benchmarkOptions() {
      return [
        { value: 'DAY', label: this.$t('flowSightMenu.benchmarkDay') },
        { value: 'WEEK', label: this.$t('flowSightMenu.benchmarkWeek') },
        { value: 'FORTNIGHT', label: this.$t('flowSightMenu.benchmarkFortnight') },
        { value: 'MONTH', label: this.$t('flowSightMenu.benchmarkMonth') },
      ]
    },
    rfqPeriodInternal: {
      get() { return this.analyticsProStore.getRfqPeriod },
      set(v) { this.analyticsProStore.setRfqPeriod(v || 'ALL') },
    },
    benchmarkInternal: {
      get() {
        const value = this.analyticsProStore.getBenchmark
        // v-btn-toggle without `mandatory` wants undefined, not 'NONE', to
        // render every button unselected.
        return value === 'NONE' ? undefined : value
      },
      set(v) { this.analyticsProStore.setBenchmark(v || 'NONE') },
    },

    // The window the current period actually covers, not just the anchor date:
    // "Dia" is one date, "Semana"/"Mês" are ranges. Empty for "Tudo", which has
    // no window to spell out - getRfqPeriodWindow returns null there.
    anchorLabel() {
      return this.formatWindow(this.analyticsProStore.getRfqPeriodWindow)
    },
    // Empty whenever no benchmark is active - getBenchmarkWindow returns null
    // for NONE, for period ALL, and for a benchmark that does not match the
    // period's granularity.
    benchmarkLabel() {
      return this.formatWindow(this.analyticsProStore.getBenchmarkWindow)
    },
    counterparties() {
      return this.analyticsProStore.getCounterparties
    },
    // Empty selection means "all" - see analyticsStore.counterpartyFilter.
    counterpartyFilterInternal: {
      get() { return this.analyticsProStore.getCounterpartyFilter },
      set(v) { this.analyticsProStore.setCounterpartyFilter(v || []) },
    },
    allCounterpartiesSelected() {
      return this.counterparties.length > 0
        && this.counterpartyFilterInternal.length === this.counterparties.length
    },
    someCounterpartiesSelected() {
      return this.counterpartyFilterInternal.length > 0 && !this.allCounterpartiesSelected
    },
    counterpartySelectAllIcon() {
      if (this.allCounterpartiesSelected) return 'mdi-close-box'
      if (this.someCounterpartiesSelected) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
  },
  methods: {
    pedirTroca(produto) {
      if (produto === this.produtoAtual) return
      // Sem dataset nao ha analise a perder: troca direto.
      if (!this.temDataset) return this.aplicarTroca(produto)
      this.produtoPendente = produto
      this.confirmandoTroca = true
    },
    cancelarTroca() {
      this.confirmandoTroca = false
      this.produtoPendente = null
    },
    confirmarTroca() {
      const produto = this.produtoPendente
      this.cancelarTroca()
      if (produto) this.aplicarTroca(produto)
    },
    aplicarTroca(produto) {
      // O store limpa o dataset e o watcher em views/FlowSight.vue leva a rota.
      this.analyticsProStore.setFlowSightProduct(produto)
    },

    // Matches on path rather than route name: the tab links are plain paths and
    // the router redirects /flowsight to /flowsight/overview.
    // Shared by the period and benchmark labels so the two can never format
    // the same kind of window differently.
    formatWindow(window) {
      if (!window) return ''
      const day = (d) => d.toLocaleDateString(this.locale)
      const from = day(window.from)
      const to = day(window.to)
      return from === to ? from : `${from} – ${to}`
    },
    isNavActive(item) {
      return this.$route.path === item.link
    },
    applyYtdShortcut() { this.$emit('apply-ytd-shortcut') },
    applyMtdShortcut() { this.$emit('apply-mtd-shortcut') },
    checkSideSwitch(side) { this.$emit('check-side-switch', side) },
    // Clearing back to [] rather than ticking all N names: both mean "all", and
    // the empty form survives a dataset swap without stranding dead names.
    // A benchmark is only comparable against its own granularity - month vs
    // last month, never month vs yesterday. 'ALL' enables nothing: there is no
    // "previous everything" to step back to.
    isBenchmarkSelectable(value) {
      return this.hasDataset && value === this.analyticsProStore.getRfqPeriod
    },
    // All-or-nothing. Empty is a real state now - no counterparty selected, so
    // no data - not a synonym for "all".
    toggleAllCounterparties() {
      this.counterpartyFilterInternal = this.allCounterpartiesSelected
        ? []
        : this.counterparties.slice()
    },
    onPeriodSliderChange() { this.$emit('on-period-slider-change') },
    fetchCsvFile() {
      if (this.octaActive) {
        this.analyticsProStore.fetchCsvDataset(this.tsMin, this.tsMax)
      }
    },
    handleTimeInput(e, isMin) {
      if (isMin) { this.tsMin = e; this.menu = false }
      else { this.tsMax = e; this.menu2 = false }
      if (this.tsMin && this.tsMax) this.fetchCsvFile()
    },
  },
}
</script>

<style scoped>
.produto-toggle {
  width: 100%;
}
.produto-btn {
  flex: 1 1 0;
  min-width: 0;
  font-size: 0.72rem;
  letter-spacing: 0;
  text-transform: none;
  border: 1px solid var(--border, #2a2e36);
  border-radius: 6px;
  opacity: 0.7;
}
.produto-btn + .produto-btn {
  margin-left: 6px;
}
.produto-btn--ativo {
  border-color: #febd0b;
  opacity: 1;
}
.produto-aviso {
  font-size: 0.68rem;
  line-height: 1.1;
  color: var(--muted, #9aa3af);
}

::v-deep .v-text-field input {
  font-size: 13px;
}

::v-deep .v-input--selection-controls__input {
  margin-right: 0 !important;
}

.v-divider {
  border-color: var(--surface-2) !important;
}

.sidebar-period {
  font-weight: bold;
  display: flex;
  flex-direction: row;
  padding-bottom: 0.2rem;
}

.card-text-desc {
  font-size: 14px;
  padding: 0 1rem;
}

.card-text-title {
  font-weight: bold;
  line-height: .6rem;
}

.nav-rail {
  display: flex;
  flex-direction: column;
  padding: 6px 6px 8px;
  gap: 2px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.nav-rail--collapsed {
  align-items: center;
  padding: 6px 2px 8px;
}

.nav-item {
  justify-content: flex-start !important;
  letter-spacing: 0;
  font-size: 12px;
  color: var(--muted) !important;
}

.nav-item--icon {
  min-width: 32px !important;
  padding: 0 !important;
  justify-content: center !important;
}

.nav-item--active {
  color: var(--main-color) !important;
  background: rgba(255, 191, 0, 0.1);
}

.period-toggle {
  display: flex;
  width: 100%;
}

/* Labels are tenor shorthand (1D / 1S / 2S / 1M), so each one clears its slot
   on a single line: the widest is ~30px against a 58px slot for the period row
   and 73px for the benchmark row. Wrapping is switched OFF on purpose - the
   long form used to break mid-word ("SEMAN / A"), and nowrap makes a future
   label that no longer fits fail visibly instead of shattering. */
.period-toggle ::v-deep .v-btn {
  flex: 1 1 0;
  min-width: 0;
  height: auto !important;
  min-height: 32px;
  padding: 4px 6px !important;
  font-size: 11px;
  line-height: 1.15;
  letter-spacing: 0;
  white-space: nowrap;
  text-align: center;
}

.period-anchor {
  font-size: 11px;
  color: var(--muted);
  padding-top: 4px;
}

.counterparty-selection {
  font-size: 13px;
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.counterparty-more {
  font-size: 12px;
  color: var(--muted);
  margin-left: 4px;
}

.uploaded-file-info {
  font-size: 12px;
  color: var(--muted);
  text-align: center;
  word-break: break-word;
}
</style>
